import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import PersonIcon from "@mui/icons-material/Person";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import dayjs, { type Dayjs } from "dayjs";

import { CircularProgressIndicator } from "../../../../../shared/components/charts";
import { DashboardCard } from "../../../../../shared/components/DashboardCard";
import {
  DateRange,
  DurationSelector,
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
} from "../../../../../shared/components";
import { useInsightsChatbotConfig, useStandardInsights } from "../../../insights";
import { useClientScope } from "../../../../../shared/contexts";
import {
  usePersistedDateRange,
  usePersistedFilters,
} from "../../../../../shared/hooks";
import PeakieWebLogo from "../../../../../assets/Peakie AI Web Logo.png";

const formatDate = (value: Dayjs) => value.format("YYYY-MM-DD");

export const PredictiveInsights: React.FC = () => {
  const theme = useTheme();
  const { effectiveClientId } = useClientScope();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { filters, setFilters } = usePersistedFilters();
  const activeFiltersCount = getAppliedFilterCount(filters);
  const { dateRange, setDateRange } = usePersistedDateRange();

  const params = useMemo(() => {
    const start = dateRange[0] ?? dayjs().startOf("month");
    const end = dateRange[1] ?? dayjs().endOf("month");
    return {
      scope_type: "org" as const,
      start_date: formatDate(start),
      end_date: formatDate(end),
      department: filters.department,
      age_range: filters.ageRange,
      sex: filters.sex?.[0],
      shift_pattern: filters.shiftPattern,
      user_ids: filters.searchUser,
      client_id: effectiveClientId || undefined,
    };
  }, [dateRange, filters, effectiveClientId]);

  const { data, isLoading, error } = useStandardInsights(params);
  const { setConfig: setChatbotConfig } = useInsightsChatbotConfig();

  const insights = data?.data;

  const overallScoreSummarySection = useMemo(() => {
    const sections = insights?.overall_wellness_score.calculation_summary_sections;
    if (!sections?.length) return null;
    return (
      sections.find((s) =>
        (s.title ?? "").toLowerCase().includes("overall score summary")
      ) ?? sections[0]
    );
  }, [insights?.overall_wellness_score.calculation_summary_sections]);

  const getSummarySection = useMemo(() => {
    const sections = insights?.overall_wellness_score.calculation_summary_sections ?? [];
    return (titleIncludes: string) => {
      const needle = titleIncludes.toLowerCase();
      return (
        sections.find((s) => (s.title ?? "").toLowerCase().includes(needle)) ??
        null
      );
    };
  }, [insights?.overall_wellness_score.calculation_summary_sections]);

  const keyHighlightsSection = useMemo(
    () => getSummarySection("key highlights"),
    [getSummarySection]
  );
  const recommendationsSection = useMemo(
    () => getSummarySection("recommendations"),
    [getSummarySection]
  );
  const areasToWatchSection = useMemo(
    () => getSummarySection("areas to watch"),
    [getSummarySection]
  );

  useEffect(() => {
    setChatbotConfig({
      title: "Peakie AI",
      initialPrompt: "What stands out this week and what should we do next?",
      buildRequest: (question) => ({
        scope: { type: "org" },
        question,
        start_date: params.start_date,
        end_date: params.end_date,
        department: params.department,
        age_range: params.age_range,
        sex: params.sex,
        shift_pattern: params.shift_pattern,
        user_ids: params.user_ids,
        client_id: params.client_id,
        k: 8,
      }),
    });
  }, [params, setChatbotConfig]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <DurationSelector
              value={dateRange}
              onChange={setDateRange}
              buttonVariant="outlined"
            />
            <Button
              variant="text"
              size="medium"
              startIcon={<FilterListIcon />}
              color={activeFiltersCount > 0 ? "primary" : "inherit"}
              onClick={() => setIsFilterOpen(true)}
            >
              {activeFiltersCount > 0 ? `FILTER (${activeFiltersCount})` : "FILTER"}
            </Button>
          </Box>
          <Box
            component="img"
            src={PeakieWebLogo}
            alt="Peakie AI"
            sx={{ height: { xs: 44, md: 56 }, width: "auto" }}
          />
        </Box>

        <FilterDrawer
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={(newFilters) => setFilters(newFilters)}
          initialFilters={filters}
        />

        {error && (
          <Alert severity="error">
            Failed to load insights.{" "}
            {error instanceof Error ? error.message : "Please try again."}
          </Alert>
        )}

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {insights && (
          <>
            <Card
              sx={{
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "none",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 3,
                    flexWrap: { xs: "wrap", md: "nowrap" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <CircularProgressIndicator
                      value={insights.overall_wellness_score.value}
                      label=""
                      icon={null}
                      color={theme.palette.success.main}
                      size={120}
                      max={100}
                    />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Overall Score Summary
                    </Typography>

                    {overallScoreSummarySection?.text ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.6 }}
                      >
                        {overallScoreSummarySection.text}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.6 }}
                      >
                        {insights.overall_wellness_score.calculation_summary_text ||
                          insights.overall_wellness_score.calculation_summary}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Alert severity="warning" icon={<WarningAmberIcon />}>
              <Typography variant="body2">
                This is not medical advice and this narrative is generated using
                AI analysis of users&apos; wearable data patterns. Please check
                for accuracy.
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <DashboardCard>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <LightbulbIcon
                      sx={{
                        color: theme.palette.success.main,
                        fontSize: 20,
                      }}
                    />
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      color="success.main"
                    >
                      Key Highlights
                    </Typography>
                  </Box>
                  {keyHighlightsSection?.items?.length ? (
                    <Stack spacing={2}>
                      {keyHighlightsSection.items.slice(0, 12).map((item, idx) => (
                        <Box
                          key={`kh-${idx}`}
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            alignItems: "flex-start",
                          }}
                        >
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              bgcolor: theme.palette.success.main,
                              mt: 1,
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {item}
                          </Typography>
                        </Box>
                      ))}
                      {keyHighlightsSection.text ? (
                        <Typography variant="body2" color="text.secondary">
                          {keyHighlightsSection.text}
                        </Typography>
                      ) : null}
                    </Stack>
                  ) : insights.key_highlights?.length ? (
                    <Stack spacing={2}>
                      {insights.key_highlights.slice(0, 6).map((h, idx) => (
                        <Box
                          key={`${h.metric_id}-${idx}`}
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            alignItems: "flex-start",
                          }}
                        >
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              bgcolor: theme.palette.success.main,
                              mt: 1,
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {h.insight ||
                              `${h.title}: ${h.value}${h.unit ? ` ${h.unit}` : ""}`}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No highlights available.
                    </Typography>
                  )}
                </DashboardCard>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <DashboardCard>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <PersonIcon
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: 20,
                      }}
                    />
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      color="primary.main"
                    >
                      Personalized Recommendations
                    </Typography>
                  </Box>
                  {recommendationsSection?.items?.length ? (
                    <Stack spacing={2}>
                      {recommendationsSection.items.slice(0, 18).map((item, idx) => {
                        const isHeading = item.trim().endsWith(":");
                        return (
                          <Box
                            key={`rec-${idx}`}
                            sx={{
                              display: "flex",
                              gap: 1.5,
                              alignItems: "flex-start",
                            }}
                          >
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                bgcolor: theme.palette.primary.main,
                                mt: 1,
                                flexShrink: 0,
                                opacity: isHeading ? 0.6 : 1,
                              }}
                            />
                            <Typography
                              variant="body2"
                              color={isHeading ? "text.primary" : "text.secondary"}
                              fontWeight={isHeading ? 700 : 400}
                            >
                              {item}
                            </Typography>
                          </Box>
                        );
                      })}
                      {recommendationsSection.text ? (
                        <Typography variant="body2" color="text.secondary">
                          {recommendationsSection.text}
                        </Typography>
                      ) : null}
                    </Stack>
                  ) : insights.personalised_recommendations?.length ? (
                    <Stack spacing={2}>
                      {insights.personalised_recommendations
                        .slice(0, 6)
                        .map((r, idx) => (
                          <Box
                            key={`${r.title}-${idx}`}
                            sx={{
                              display: "flex",
                              gap: 1.5,
                              alignItems: "flex-start",
                            }}
                          >
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                bgcolor: theme.palette.primary.main,
                                mt: 1,
                                flexShrink: 0,
                              }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {r.title}
                            </Typography>
                          </Box>
                        ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No recommendations available.
                    </Typography>
                  )}
                </DashboardCard>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <DashboardCard>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <WarningAmberIcon
                      sx={{
                        color: theme.palette.warning.main,
                        fontSize: 20,
                      }}
                    />
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      color="warning.main"
                    >
                      Areas to Watch
                    </Typography>
                  </Box>
                  {areasToWatchSection?.items?.length ? (
                    <Stack spacing={2}>
                      {areasToWatchSection.items.slice(0, 12).map((item, idx) => (
                        <Box
                          key={`watch-${idx}`}
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            alignItems: "flex-start",
                          }}
                        >
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              bgcolor: theme.palette.warning.main,
                              mt: 1,
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {item}
                          </Typography>
                        </Box>
                      ))}
                      {areasToWatchSection.text ? (
                        <Typography variant="body2" color="text.secondary">
                          {areasToWatchSection.text}
                        </Typography>
                      ) : null}
                    </Stack>
                  ) : insights.areas_to_watch?.length ? (
                    <Stack spacing={2}>
                      {insights.areas_to_watch.slice(0, 6).map((a, idx) => (
                        <Box
                          key={`${a.metric_id}-${idx}`}
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            alignItems: "flex-start",
                          }}
                        >
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              bgcolor: theme.palette.warning.main,
                              mt: 1,
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {a.reason}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No watch areas available.
                    </Typography>
                  )}
                </DashboardCard>
              </Grid>
            </Grid>
          </>
        )}
      </Stack>
    </Box>
  );
};
